import Swal from 'sweetalert2';
import generateAiBackground from './generateAiBackground';
import openBackgroundBuilderModal from './builder/builderModal';
import { startTimedLoader } from './timedProgressLoader';
import { setCanvasBackground } from '~src/image';
import { createBackgroundRecordFromUrl } from '~src/backgrounds/backgroundStorage';
import { handleSidebarBackgrounds } from '~src/sidebar/handleSidebarBackgrounds';
import { triggerStateChangedRequest } from '~core';
import { onErrorShowMessage } from '~src/errors';
import { trackEvent } from '~src/tracking';
import { checkAiCreditsBeforeUse } from '~core/aiCredits';
import canCreateBackgroundWithAi from './canCreateBackgroundWithAi';
import { getImageDimensionsFromUrl } from '~src/utils/getImageDimensionsFromUrl';

// Proprietà di tracking derivate dalle scelte del builder: servono a
// misurare la varietà d’uso reale (spec §Tracking).
const builderTrackingProps = (choice) => ({
  essence: choice.base.type,
  textReadability: choice.textReadability === true,
  technique: choice.style.technique,
  // I nove campi custom* riportano il testo così com'è in stato (mai
  // undefined, stringa vuota se assente) anche quando la selezione gemella
  // non è 'custom': per sapere se contribuiscono davvero al prompt vanno
  // incrociati con quella selezione (es. `technique === 'custom'`,
  // `sceneKey === 'custom'`, …).
  customTechnique: choice.style.customTechnique || '',
  intensity: choice.style.intensity,
  customIntensity: choice.style.customIntensity || '',
  palette: choice.style.palette,
  customPalette: choice.style.customPalette || '',
  atmosphere: choice.style.atmosphere,
  customAtmosphere: choice.style.customAtmosphere || '',
  gradientDirection: choice.base.direction,
  gradientSpread: choice.base.spread,
  gradientIntensity: choice.base.gradientIntensity,
  gradientFrom: choice.base.fromColor
    ? choice.base.fromColor.key || choice.base.fromColor.hex
    : null,
  gradientTo: choice.base.toColor
    ? choice.base.toColor.key || choice.base.toColor.hex
    : 'white',
  // Tipo, attivazione e grana distinguono l’uso reale della carta dalla sola
  // apertura della macro e rendono misurabile la scala di intensità.
  paper: choice.base.paper,
  paperOn: choice.base.paperOn,
  grain: choice.base.grain,
  // Materiale e intensità sono un asse nuovo: misurarli separatamente evita
  // di sapere soltanto quante volte è stata aperta la macro «Materiali».
  material: choice.base.material,
  materialIntensity: choice.base.materialIntensity,
  scene: choice.base.sceneKey,
  customScene: choice.base.customScene || '',
  scenePlacement: choice.base.scenePlacement,
  sceneExtent: choice.base.sceneExtent,
  sceneFade: choice.base.sceneFade,
  sacredFigure: choice.base.sacredFigure,
  customSacredFigure: choice.base.customSacredFigure || '',
  sacredFraming: choice.base.sacredFraming,
  customSacredFraming: choice.base.customSacredFraming || '',
  sacredPerspective: choice.base.sacredPerspective,
  customSacredPerspective: choice.base.customSacredPerspective || '',
  sacredMood: choice.base.sacredMood,
  customSacredMood: choice.base.customSacredMood || '',
  elementsCount: choice.elements.length,
  frameStyle: choice.frame.style,
  frameCoverage: choice.frame.coverage,
  frameRelation: choice.frame.relation,
});

// Preview the generated background and confirm.
const previewAndConfirm = (imageUrl) =>
  Swal.fire({
    title: 'Anteprima sfondo',
    imageUrl,
    imageAlt: 'Sfondo generato',
    imageWidth: 360,
    showCancelButton: true,
    confirmButtonText: 'Salva e applica',
    cancelButtonText: 'Annulla',
  }).then((result) => result.isConfirmed);

export default async function handleCreateBackgroundWithAi() {
  const allowed = await canCreateBackgroundWithAi();
  if (!allowed) {
    alert('Questa funzione non è disponibile nel tuo piano di abbonamento');
    return;
  }

  const choice = await openBackgroundBuilderModal();
  if (!choice) {
    return;
  }

  const hasCredits = await checkAiCreditsBeforeUse({
    featureName: 'Crea sfondo con IA',
    requiredCredits: 2,
  });
  if (!hasCredits) {
    return;
  }

  const width = Math.round(window.canvas.width);
  const height = Math.round(window.canvas.height);

  const loader = startTimedLoader({
    title: 'Generazione sfondo in corso',
    messages: [
      { text: 'Preparazione del prompt...' },
      { text: 'Generazione dello sfondo...' },
      { text: 'Rifinitura dei dettagli...' },
      { text: 'Ultimi ritocchi...' },
    ],
    durationMs: 180000,
  });
  let generatedImage;
  try {
    const generatedBackground = await generateAiBackground({
      prompt: choice.prompt,
      width,
      height,
    });
    const imageUrl = generatedBackground.imageUrl;
    if (!imageUrl) {
      throw new Error('AI background response missing imageUrl');
    }

    const {
      height: generatedHeight,
      width: generatedWidth,
    } = await getImageDimensionsFromUrl(imageUrl);
    if (!(generatedWidth > 0) || !(generatedHeight > 0)) {
      throw new Error('AI background has invalid image dimensions');
    }

    generatedImage = {
      height: generatedHeight,
      imageUrl,
      width: generatedWidth,
    };
  } catch (err) {
    console.error(err);
    loader.close();
    trackEvent('AI Background', {
      success: false,
      ...builderTrackingProps(choice),
    });
    return onErrorShowMessage();
  }
  loader.complete();
  loader.close();

  const confirmed = await previewAndConfirm(generatedImage.imageUrl);
  if (!confirmed) {
    return;
  }

  try {
    Swal.fire({
      title: 'Salvataggio...',
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    const background = await createBackgroundRecordFromUrl({
      height: generatedImage.height,
      imageUrl: generatedImage.imageUrl,
      tags: [],
      width: generatedImage.width,
    });
    const backgroundUrl = background?.file?.url || generatedImage.imageUrl;

    await handleSidebarBackgrounds(true);
    await setCanvasBackground(backgroundUrl, {
      allowUpscale: true,
      sourceDimensions: {
        width: background?.width || generatedImage.width,
        height: background?.height || generatedImage.height,
      },
      targetDimensions: {
        width,
        height,
      },
    });

    window.canvas.renderAll();
    triggerStateChangedRequest();

    trackEvent('AI Background', {
      success: true,
      ...builderTrackingProps(choice),
    });
    Swal.fire({
      icon: 'success',
      title: 'Sfondo creato e applicato!',
      timer: 2500,
      showConfirmButton: false,
    });
  } catch (err) {
    console.error('Error saving/applying AI background:', err);
    Swal.close();
    trackEvent('AI Background', {
      success: false,
      ...builderTrackingProps(choice),
    });
    return onErrorShowMessage();
  }
}
